import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import SEO from "../components/SEO";
import { api, API } from "../lib/api";

/**
 * Investor pitch deck — full-bleed, keyboard-navigable, brand-aligned.
 * Cream/ink/forest palette matching the rest of the app, with forest
 * photography on hero slides and bold accent colours throughout.
 *
 * All copy is i18n-driven (see i18n_pitch.js) — EN / PT / ES / FR / AR.
 *
 * Keyboard: ← →  Space  Esc        URL: /pitch  (jump via ?slide=N)
 */
export default function Pitch() {
  const { t } = useTranslation();
  const tr = useCallback((k, o) => t(`pitch.${k}`, o), [t]);
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(() => {
    const n = parseInt(new URLSearchParams(window.location.search).get("slide") || "1", 10);
    return Number.isFinite(n) && n >= 1 ? Math.min(n - 1, 9) : 0;
  });

  useEffect(() => {
    api.get("/pitch/data").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  const slides = useMemo(() => buildSlides(data || {}, tr), [data, tr]);

  const go = useCallback((delta) => {
    setIdx((i) => Math.max(0, Math.min(slides.length - 1, i + delta)));
  }, [slides.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); go(-1); }
      else if (e.key === "Home") { setIdx(0); }
      else if (e.key === "End") { setIdx(slides.length - 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, slides.length]);

  useEffect(() => {
    const u = new URL(window.location.href);
    u.searchParams.set("slide", idx + 1);
    window.history.replaceState({}, "", u.toString());
  }, [idx]);

  const current = slides[idx];

  return (
    <div className="fixed inset-0 bg-bg text-ink overflow-hidden font-sans" data-testid="pitch-deck">
      <SEO
        title="Investor Pitch Deck"
        description="GreenBelt investor pitch — the regulated exchange for the Brazilian UCS carbon asset. €3M seed round · live in production · BMV Global + B3 partnerships."
        path="/pitch"
        type="article"
      />
      {/* slide */}
      <div key={idx} className="absolute inset-0 animate-[slideIn_0.5s_cubic-bezier(0.16,1,0.3,1)]" data-testid={`slide-${idx + 1}`}>
        {current}
      </div>

      {/* top bar */}
      <div className="absolute top-0 inset-x-0 h-1 bg-leaf/15 z-30">
        <div className="h-full bg-leaf transition-all duration-500" style={{ width: `${((idx + 1) / slides.length) * 100}%` }}></div>
      </div>
      <div className="absolute top-6 left-10 lg:left-20 flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.24em] text-ink2 z-30">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-leaf animate-pulse"></span>
        <span>{tr("top_label")}</span>
      </div>
      <div className="absolute top-6 right-10 lg:right-20 flex items-center gap-4 text-[10px] font-mono uppercase tracking-[0.24em] text-ink2 z-30">
        <span>{String(idx + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}</span>
        <a href={`${API}/pitch/export.pdf`} target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ink" data-testid="pitch-download">{tr("pdf_download")}</a>
        <Link to="/" className="underline underline-offset-4 hover:text-ink" data-testid="pitch-exit">{tr("exit")}</Link>
      </div>

      {/* bottom nav */}
      <div className="absolute bottom-8 inset-x-0 flex items-center justify-center gap-4 text-xs font-mono uppercase tracking-[0.2em] z-30">
        <button onClick={() => go(-1)} disabled={idx === 0}
          className="border border-ink/30 px-5 py-2 hover:border-ink hover:bg-ink hover:text-bg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="pitch-prev">{tr("prev")}</button>
        <div className="flex gap-1.5">
          {slides.map((slide, i) => (
            <button key={slide.key || `slide-${i + 1}`} onClick={() => setIdx(i)}
              className={`h-1.5 transition-all ${i === idx ? "bg-leaf w-8" : "bg-ink/20 w-3 hover:bg-ink/50"}`}
              data-testid={`pitch-dot-${i + 1}`} aria-label={`Slide ${i + 1}`}></button>
          ))}
        </div>
        <button onClick={() => go(1)} disabled={idx === slides.length - 1}
          className="bg-leaf text-bg border border-leaf px-5 py-2 hover:bg-ink hover:border-ink transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="pitch-next">{tr("next")}</button>
      </div>

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; } to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ---------------- shared building blocks ----------------
const FOREST_HERO = "https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=2400&q=70";
const CANOPY_HERO = "https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?auto=format&fit=crop&w=2400&q=70";
const RIVER_HERO  = "https://images.unsplash.com/photo-1565117661210-2e2c2c8e23f8?auto=format&fit=crop&w=2400&q=70";

function SlideShell({ children, className = "", padded = true }) {
  return (
    <div className={`absolute inset-0 ${padded ? "px-10 lg:px-20 py-16 lg:py-24" : ""} flex flex-col ${className}`}>{children}</div>
  );
}

function Eyebrow({ children, n }) {
  return (
    <div className="font-mono text-xs uppercase tracking-[0.32em] text-leaf mb-6">
      <span className="text-ink/40">{String(n).padStart(2, "0")}</span> <span className="text-leaf">·</span> <span className="text-ink/80">{children}</span>
    </div>
  );
}
function H1({ children }) {
  return <h1 className="font-serif text-5xl lg:text-7xl tracking-tight text-ink leading-[1.05]">{children}</h1>;
}
function H2({ children }) {
  return <h2 className="font-serif text-3xl lg:text-5xl tracking-tight text-ink leading-tight">{children}</h2>;
}
function Body({ children, className = "" }) {
  return <p className={`text-base lg:text-lg text-ink/80 leading-relaxed ${className}`}>{children}</p>;
}
function Pull({ children }) {
  return <p className="font-serif italic text-2xl lg:text-3xl text-ink leading-snug">{children}</p>;
}

/** Big number tile in the brand cream + ink palette */
function Stat({ label, value, sub, accent = false }) {
  return (
    <div className={`p-5 ${accent ? "bg-ink text-bg" : "bg-bg border border-line"}`}>
      <div className={`font-mono text-[10px] uppercase tracking-[0.22em] mb-3 ${accent ? "text-leaf-light" : "text-ink2"}`}>{label}</div>
      <div className={`font-mono text-3xl lg:text-4xl ${accent ? "text-bg" : "text-ink"}`}>{value}</div>
      {sub && <div className={`text-xs mt-3 ${accent ? "text-bg/60" : "text-ink2"}`}>{sub}</div>}
    </div>
  );
}

function fmt(n, max = 0) {
  if (n == null) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: max });
}

// ---------------- slides ----------------
function buildSlides(d, tr) {
  const proj = d.projects || {};
  const idx = d.index?.spot || {};
  const perTon = tr("s1.per_tco2");

  return [
    // ====== 1 — Title slide (hero image left, content right) ======
    <SlideShell key="s1" padded={false}>
      <div className="absolute inset-0 grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-5 relative overflow-hidden hidden lg:block">
          <img src={FOREST_HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/30 via-ink/10 to-bg"></div>
          <div className="absolute bottom-10 left-10 text-bg">
            <div className="label-tag !text-leaf-light">{tr("s1.hero_stat")}</div>
            <div className="font-serif text-2xl mt-2 max-w-xs leading-snug">{tr("s1.hero_caption")}</div>
          </div>
        </div>
        <div className="lg:col-span-7 px-10 lg:px-20 py-16 lg:py-24 flex flex-col justify-center">
          <Eyebrow n={1}>{tr("s1.eyebrow")}</Eyebrow>
          <H1>{tr("s1.title_a")}<br/><span className="text-leaf">{tr("s1.title_em")}</span><br/>{tr("s1.title_b")}</H1>
          <div className="mt-8 max-w-2xl"><Body>
            {tr("s1.body_pre")}<span className="text-leaf font-medium">{tr("s1.body_em")}</span>{tr("s1.body_post")}
          </Body></div>
          <div className="mt-10 grid grid-cols-3 gap-3 max-w-xl">
            <Stat label={tr("s1.spot_brl")} value={idx.BRL ? `R$ ${fmt(idx.BRL, 2)}` : "—"} sub={perTon} />
            <Stat label={tr("s1.spot_usd")} value={idx.USD ? `$${fmt(idx.USD, 2)}` : "—"} sub={perTon} accent />
            <Stat label={tr("s1.spot_eur")} value={idx.EUR ? `€${fmt(idx.EUR, 2)}` : "—"} sub={perTon} />
          </div>
        </div>
      </div>
    </SlideShell>,

    // ====== 2 — Problem ======
    <SlideShell key="s2">
      <div className="flex-1 flex flex-col justify-center max-w-6xl">
        <Eyebrow n={2}>{tr("s2.eyebrow")}</Eyebrow>
        <H2>{tr("s2.h2_a")} <span className="text-leaf line-through decoration-leaf/40">{tr("s2.h2_credible")}</span> <span className="text-ink">{tr("s2.h2_broken")}</span>{tr("s2.h2_dot")}</H2>
        <div className="mt-10 grid grid-cols-12 gap-8 max-w-5xl">
          <div className="col-span-12 lg:col-span-7">
            <Body>
              {tr("s2.body_pre")}<b className="text-ink">{tr("s2.body_em1")}</b>{tr("s2.body_mid")}<b className="text-ink">{tr("s2.body_em2")}</b>{tr("s2.body_post")}
            </Body>
          </div>
          <div className="col-span-12 lg:col-span-5 space-y-3">
            {[
              [tr("s2.pain1_t"), tr("s2.pain1_s")],
              [tr("s2.pain2_t"), tr("s2.pain2_s")],
              [tr("s2.pain3_t"), tr("s2.pain3_s")],
              [tr("s2.pain4_t"), tr("s2.pain4_s")],
            ].map(([t, sub]) => (
              <div key={t} className="border-l-2 border-leaf pl-4">
                <div className="text-ink font-medium">{t}</div>
                <div className="text-xs text-ink2 font-mono uppercase tracking-wider mt-1">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideShell>,

    // ====== 3 — Solution ======
    <SlideShell key="s3" className="bg-muted">
      <div className="flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full">
        <Eyebrow n={3}>{tr("s3.eyebrow")}</Eyebrow>
        <H2>{tr("s3.h2_a")}<br/><span className="text-leaf">{tr("s3.h2_em")}</span> {tr("s3.h2_b")}</H2>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {[
            ["01", tr("s3.p1_h"), tr("s3.p1_b")],
            ["02", tr("s3.p2_h"), tr("s3.p2_b")],
            ["03", tr("s3.p3_h"), tr("s3.p3_b")],
          ].map(([num, head, body]) => (
            <div key={num} className="bg-bg border border-line p-6 hover:border-leaf transition-colors">
              <div className="font-mono text-5xl text-leaf">{num}</div>
              <div className="font-serif text-2xl mt-4 text-ink">{head}</div>
              <div className="text-sm text-ink2 mt-3 leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>,

    // ====== 4 — Product ======
    <SlideShell key="s4">
      <div className="flex-1 flex flex-col justify-center max-w-6xl">
        <Eyebrow n={4}>{tr("s4.eyebrow")}</Eyebrow>
        <H2>{tr("s4.h2_a")} <span className="text-leaf">{tr("s4.h2_em")}</span>{tr("s4.h2_b")}</H2>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-4 text-base">
          {[
            [tr("s4.f1_t"), tr("s4.f1_s"), "01"],
            [tr("s4.f2_t"), tr("s4.f2_s"), "02"],
            [tr("s4.f3_t"), tr("s4.f3_s"), "03"],
            [tr("s4.f4_t"), tr("s4.f4_s"), "04"],
            [tr("s4.f5_t"), tr("s4.f5_s"), "05"],
            [tr("s4.f6_t"), tr("s4.f6_s"), "06"],
            [tr("s4.f7_t"), tr("s4.f7_s"), "07"],
            [tr("s4.f8_t"), tr("s4.f8_s"), "08"],
          ].map(([t, sub, n]) => (
            <div key={t} className="flex gap-4 py-3 border-b border-line">
              <div className="font-mono text-leaf text-sm">{n}</div>
              <div>
                <div className="text-ink font-medium">{t}</div>
                <div className="text-ink2 text-sm mt-1">{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>,

    // ====== 5 — Differentiators (dark accent) ======
    <SlideShell key="s5" className="bg-ink text-bg" padded={false}>
      <div className="absolute inset-0 px-10 lg:px-20 py-16 lg:py-24 flex flex-col justify-center max-w-7xl mx-auto w-full">
        <div className="font-mono text-xs uppercase tracking-[0.32em] text-leaf-light mb-6">
          <span className="text-bg/40">05</span> · <span className="text-bg/80">{tr("s5.eyebrow")}</span>
        </div>
        <h2 className="font-serif text-3xl lg:text-5xl tracking-tight leading-tight">{tr("s5.h2_a")} <span className="text-leaf-light">{tr("s5.h2_em")}</span> {tr("s5.h2_b")}</h2>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-5">
          {[
            [tr("s5.d1_t"), tr("s5.d1_s"), "🇧🇷"],
            [tr("s5.d2_t"), tr("s5.d2_s"), "ISIN"],
            [tr("s5.d3_t"), tr("s5.d3_s"), "✓"],
            [tr("s5.d4_t"), tr("s5.d4_s"), "$£€"],
            [tr("s5.d5_t"), tr("s5.d5_s"), "30s"],
          ].map(([t, sub, mark]) => (
            <div key={t} className="border border-bg/15 p-5 bg-bg/[0.04] hover:bg-bg/10 transition-colors">
              <div className="font-mono text-2xl text-leaf-light mb-4">{mark}</div>
              <div className="text-bg font-mono text-sm uppercase tracking-wider">{t}</div>
              <div className="text-bg/65 text-sm mt-3 leading-relaxed">{sub}</div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>,

    // ====== 6 — Live network (canopy backdrop) ======
    <SlideShell key="s6" padded={false}>
      <div className="absolute inset-0">
        <img src={CANOPY_HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-bg via-bg/85 to-bg"></div>
      </div>
      <div className="relative px-10 lg:px-20 py-16 lg:py-24 flex flex-col justify-center max-w-6xl mx-auto w-full flex-1">
        <Eyebrow n={6}>{tr("s6.eyebrow")}</Eyebrow>
        <H2>{tr("s6.h2_a")} <span className="text-leaf">{tr("s6.h2_em")}</span>{tr("s6.h2_b")}</H2>
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label={tr("s6.stat1_l")} value={fmt(proj.count)} sub={tr("s6.stat1_s")} />
          <Stat label={tr("s6.stat2_l")} value={fmt(proj.total_hectares, 0)} sub={tr("s6.stat2_s")} accent />
          <Stat label={tr("s6.stat3_l")} value={fmt(proj.total_tons_available, 0)} sub={tr("s6.stat3_s")} />
          <Stat label={tr("s6.stat4_l")} value={`$${fmt(proj.avg_price_usd_per_ton, 2)}`} sub={tr("s6.stat4_s")} accent />
        </div>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
          <div className="border border-line bg-bg/80 backdrop-blur p-4">
            <div className="font-mono text-[10px] text-leaf uppercase tracking-widest mb-2">{tr("s6.audit_label")}</div>
            <div className="text-ink">{tr("s6.audit_val")}</div>
          </div>
          <div className="border border-line bg-bg/80 backdrop-blur p-4">
            <div className="font-mono text-[10px] text-leaf uppercase tracking-widest mb-2">{tr("s6.biomes_label")}</div>
            <div className="text-ink">{tr("s6.biomes_val")}</div>
          </div>
          <div className="border border-line bg-bg/80 backdrop-blur p-4">
            <div className="font-mono text-[10px] text-leaf uppercase tracking-widest mb-2">{tr("s6.producer_label")}</div>
            <div className="text-ink">{tr("s6.producer_val")}</div>
          </div>
        </div>
      </div>
    </SlideShell>,

    // ====== 7 — Market ======
    <SlideShell key="s7">
      <div className="flex-1 flex flex-col justify-center max-w-6xl w-full">
        <Eyebrow n={7}>{tr("s7.eyebrow")}</Eyebrow>
        <H2>{tr("s7.h2_a")} <span className="text-ink2">{tr("s7.h2_today")}</span> {tr("s7.h2_to")} <span className="text-leaf">{tr("s7.h2_2030")}</span>{tr("s7.h2_dot")}</H2>
        <div className="mt-8 max-w-3xl"><Body>
          {tr("s7.body_pre")}<b className="text-ink">{tr("s7.body_em")}</b>{tr("s7.body_post")}
        </Body></div>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label={tr("s7.stat1_l")} value="$2B" sub={tr("s7.stat1_s")} />
          <Stat label={tr("s7.stat2_l")} value="$50B" sub={tr("s7.stat2_s")} accent />
          <Stat label={tr("s7.stat3_l")} value="40%" sub={tr("s7.stat3_s")} />
          <Stat label={tr("s7.stat4_l")} value="0%" sub={tr("s7.stat4_s")} />
        </div>
        <div className="mt-10 border-l-4 border-leaf pl-6 max-w-3xl">
          <Pull>{tr("s7.pull")}</Pull>
        </div>
      </div>
    </SlideShell>,

    // ====== 8 — Business model ======
    <SlideShell key="s8" className="bg-muted">
      <div className="flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full">
        <Eyebrow n={8}>{tr("s8.eyebrow")}</Eyebrow>
        <H2>{tr("s8.h2_a")} <span className="text-leaf">{tr("s8.h2_em")}</span>{tr("s8.h2_b")}</H2>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {[
            ["3.0%", tr("s8.r1_l"), tr("s8.r1_s"), false],
            ["1.5%", tr("s8.r2_l"), tr("s8.r2_s"), true],
            ["4% + retainer", tr("s8.r3_l"), tr("s8.r3_s"), false],
          ].map(([num, label, sub, accent]) => (
            <div key={label} className={`p-7 border ${accent ? "bg-ink text-bg border-ink" : "bg-bg border-line"}`}>
              <div className={`font-mono text-5xl ${accent ? "text-leaf-light" : "text-leaf"}`}>{num}</div>
              <div className={`font-mono text-sm mt-5 uppercase tracking-wider ${accent ? "text-bg" : "text-ink"}`}>{label}</div>
              <div className={`text-sm mt-3 leading-relaxed ${accent ? "text-bg/70" : "text-ink2"}`}>{sub}</div>
            </div>
          ))}
        </div>
        <div className="mt-8 text-sm text-ink2 max-w-3xl">
          <span className="text-leaf font-mono text-xs uppercase tracking-wider">{tr("s8.future_label")}</span>
          &nbsp; {tr("s8.future_val")}
        </div>
      </div>
    </SlideShell>,

    // ====== 9 — Partners & traction (river backdrop) ======
    <SlideShell key="s9" padded={false}>
      <div className="absolute inset-0">
        <img src={RIVER_HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-bg via-bg/90 to-bg/85"></div>
      </div>
      <div className="relative px-10 lg:px-20 py-16 lg:py-24 flex flex-col justify-center max-w-6xl mx-auto w-full flex-1">
        <Eyebrow n={9}>{tr("s9.eyebrow")}</Eyebrow>
        <H2>{tr("s9.h2_a")} <span className="text-leaf">{tr("s9.h2_em")}</span>{tr("s9.h2_b")}</H2>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-bg/85 backdrop-blur border border-line p-6">
            <div className="font-mono text-xs text-leaf uppercase tracking-[0.22em] mb-5">{tr("s9.strategic_partners")}</div>
            <ul className="space-y-3 text-ink text-[15px]">
              <li className="flex gap-3"><span className="text-leaf font-mono text-xs mt-1">●</span><span><Trans i18nKey="pitch.s9.sp1" components={{ b: <b /> }} /></span></li>
              <li className="flex gap-3"><span className="text-leaf font-mono text-xs mt-1">●</span><span><Trans i18nKey="pitch.s9.sp2" components={{ b: <b /> }} /></span></li>
              <li className="flex gap-3"><span className="text-leaf/40 font-mono text-xs mt-1">○</span><span><Trans i18nKey="pitch.s9.sp3_a" components={{ b: <b /> }} /> <span className="text-ink2 text-xs">{tr("s9.sp3_note")}</span>{tr("s9.sp3_b")}</span></li>
              <li className="flex gap-3"><span className="text-leaf font-mono text-xs mt-1">●</span><span><Trans i18nKey="pitch.s9.sp4" components={{ b: <b /> }} /></span></li>
            </ul>
          </div>
          <div className="bg-bg/85 backdrop-blur border border-line p-6">
            <div className="font-mono text-xs text-leaf uppercase tracking-[0.22em] mb-5">{tr("s9.platform_traction")}</div>
            <ul className="space-y-3 text-ink text-[15px]">
              <li className="flex gap-3"><span className="text-leaf font-mono text-xs mt-1">✓</span><span>{tr("s9.t1_a")} <span className="text-leaf">{tr("s9.t1_url")}</span></span></li>
              <li className="flex gap-3"><span className="text-leaf font-mono text-xs mt-1">✓</span><span>{tr("s9.t2_stats", { count: fmt(proj.count), hectares: fmt(proj.total_hectares, 0), tons: fmt(proj.total_tons_available, 0) })}</span></li>
              <li className="flex gap-3"><span className="text-leaf font-mono text-xs mt-1">✓</span><span>{tr("s9.t3")}</span></li>
              <li className="flex gap-3"><span className="text-leaf font-mono text-xs mt-1">✓</span><span>{tr("s9.t4")}</span></li>
              <li className="flex gap-3"><span className="text-leaf font-mono text-xs mt-1">✓</span><span>{tr("s9.t5")}</span></li>
            </ul>
          </div>
        </div>
      </div>
    </SlideShell>,

    // ====== 10 — Ask ======
    <SlideShell key="s10" className="bg-ink text-bg" padded={false}>
      <div className="absolute inset-0 px-10 lg:px-20 py-16 lg:py-24 flex flex-col justify-center max-w-6xl mx-auto w-full">
        <div className="font-mono text-xs uppercase tracking-[0.32em] mb-6">
          <span className="text-bg/40">10</span> <span className="text-leaf-light">·</span> <span className="text-bg/80">{tr("s10.eyebrow")}</span>
        </div>
        <h1 className="font-serif text-5xl lg:text-7xl tracking-tight leading-[1.05]">
          {tr("s10.h1_a")} <span className="text-leaf-light">{tr("s10.h1_em")}</span> {tr("s10.h1_b")}
        </h1>
        <div className="mt-8 max-w-3xl text-base lg:text-lg text-bg/80 leading-relaxed">
          {tr("s10.sub")}
        </div>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {[
            ["60%", tr("s10.a1_t"), tr("s10.a1_s")],
            ["25%", tr("s10.a2_t"), tr("s10.a2_s")],
            ["15%", tr("s10.a3_t"), tr("s10.a3_s")],
          ].map(([n, t, sub]) => (
            <div key={t} className="border border-bg/15 p-6 bg-bg/[0.04]">
              <div className="font-mono text-5xl text-leaf-light">{n}</div>
              <div className="text-bg font-mono text-sm mt-4 uppercase tracking-wider">{t}</div>
              <div className="text-bg/65 text-sm mt-3 leading-relaxed">{sub}</div>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap items-center gap-6">
          <a href="mailto:investors@greenbelt.io" className="bg-leaf-light text-ink px-7 py-3.5 font-mono uppercase tracking-[0.18em] text-sm hover:bg-bg transition-colors" data-testid="pitch-contact">
            {tr("s10.contact")}
          </a>
          <Link to="/demo" className="text-bg/80 hover:text-bg underline underline-offset-4 font-mono uppercase tracking-[0.18em] text-sm" data-testid="pitch-demo-cta">
            {tr("s10.demo_cta")}
          </Link>
        </div>
      </div>
    </SlideShell>,
  ];
}
